import { useEffect, useState, type ChangeEvent } from 'react';
import { Icon } from '../components/Icon';
import { api, ApiError } from '../lib/api';
import { date, initials, tone } from '../lib/format';
import { useSession } from '../lib/session';
import '../styles/crm.css';

interface Company {
  id: string;
  legalName: string;
  tradeName: string | null;
  jurisdiction: string | null;
  country: string | null;
  functionalCurrency: string;
  licenseNumber: string | null;
  licenseAuthority: string | null;
  licenseExpiryDate: string | null;
  activities: string[];
  sectors: string[];
}

function daysTo(iso: string | null): number | null {
  if (!iso) return null;
  return Math.round((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

function CreateModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [f, setF] = useState({ legalName: '', tradeName: '', jurisdiction: '', functionalCurrency: 'AED', licenseNumber: '', licenseExpiryDate: '', activities: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    if (f.legalName.trim().length < 2) { setErr('The legal name is required.'); return; }
    setBusy(true);
    setErr(null);
    try {
      await api.post('/companies', {
        legalName: f.legalName.trim(),
        tradeName: f.tradeName.trim() || undefined,
        jurisdiction: f.jurisdiction.trim() || undefined,
        functionalCurrency: f.functionalCurrency.trim() || 'AED',
        licenseNumber: f.licenseNumber.trim() || undefined,
        licenseExpiryDate: f.licenseExpiryDate || undefined,
        activities: f.activities.split(',').map((a) => a.trim()).filter(Boolean),
      });
      onCreated();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'No se pudo crear la empresa.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-scrim" onMouseDown={onClose}>
      <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal__head">
          <div><span className="eyebrow">Nueva</span><h2 className="h2" style={{ marginTop: 4, fontSize: 20 }}>Alta de empresa</h2></div>
          <button className="icon-btn" onClick={onClose}><Icon name="x" size={18} /></button>
        </div>
        <div className="modal__body">
          <div className="field"><label>Legal name</label><input className="input" value={f.legalName} onChange={set('legalName')} /></div>
          <div className="field"><label>Nombre comercial</label><input className="input" value={f.tradeName} onChange={set('tradeName')} /></div>
          <div className="row gap-12">
            <div className="field grow"><label>Jurisdiction</label><input className="input" value={f.jurisdiction} onChange={set('jurisdiction')} placeholder="p. ej. DMCC" /></div>
            <div className="field" style={{ width: 110 }}><label>Moneda</label><input className="input" value={f.functionalCurrency} onChange={set('functionalCurrency')} /></div>
          </div>
          <div className="row gap-12">
            <div className="field grow"><label>Nº de licencia</label><input className="input" value={f.licenseNumber} onChange={set('licenseNumber')} /></div>
            <div className="field" style={{ width: 160 }}><label>Caduca</label><input className="input" type="date" value={f.licenseExpiryDate} onChange={set('licenseExpiryDate')} /></div>
          </div>
          <div className="field"><label>Actividades (separadas por comas)</label><input className="input" value={f.activities} onChange={set('activities')} placeholder="IT Consultancy, Software Development" /></div>
          {err && <div className="act-error"><Icon name="alert" size={16} />{err}</div>}
        </div>
        <div className="modal__foot">
          <button className="btn btn--ghost" onClick={onClose}>Cancelar</button>
          <button className="btn btn--primary" onClick={submit} disabled={busy}>{busy ? 'Creando…' : 'Crear empresa'}</button>
        </div>
      </div>
    </div>
  );
}

function BrandingModal({ companyId, name, onClose }: { companyId: string; name: string; onClose: () => void }) {
  const [form, setForm] = useState<{
    logoUrl: string; stampUrl: string; companySignatureUrl: string;
    brandColor: string; signatoryName: string; signatoryTitle: string;
    documentFooter: string; bankLine: string;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get<Record<string, string | null>>(`/companies/${companyId}/branding`).then((b) => setForm({
      logoUrl: b.logoUrl ?? '', stampUrl: b.stampUrl ?? '', companySignatureUrl: b.companySignatureUrl ?? '',
      brandColor: b.brandColor ?? '#111111', signatoryName: b.signatoryName ?? '',
      signatoryTitle: b.signatoryTitle ?? '', documentFooter: b.documentFooter ?? '', bankLine: b.bankLine ?? '',
    })).catch(() => setForm({
      logoUrl: '', stampUrl: '', companySignatureUrl: '', brandColor: '#111111',
      signatoryName: '', signatoryTitle: '', documentFooter: '', bankLine: '',
    }));
  }, [companyId]);

  // Convierte un archivo de imagen en data:URL base64 para guardarlo.
  const readImage = (file: File): Promise<string> => new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) { reject(new Error('El archivo debe ser una imagen.')); return; }
    if (file.size > 2 * 1024 * 1024) { reject(new Error('La imagen no debe superar 2 MB.')); return; }
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error('No se pudo leer el archivo.'));
    r.readAsDataURL(file);
  });

  const onPickImage = async (field: 'logoUrl' | 'stampUrl' | 'companySignatureUrl', file: File | undefined) => {
    if (!file || !form) return;
    setErr(null);
    try { const data = await readImage(file); setForm({ ...form, [field]: data }); }
    catch (e) { setErr(e instanceof Error ? e.message : 'Imagen no válida.'); }
  };

  const save = async () => {
    if (!form) return;
    setBusy(true); setErr(null); setSaved(false);
    try {
      await api.patch(`/companies/${companyId}/branding`, {
        ...form,
        logoUrl: form.logoUrl.trim() || null,
        stampUrl: form.stampUrl.trim() || null,
        companySignatureUrl: form.companySignatureUrl.trim() || null,
      });
      setSaved(true);
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'No se pudo guardar.'); }
    finally { setBusy(false); }
  };

  const ImageField = ({ field, label, hint }: { field: 'logoUrl' | 'stampUrl' | 'companySignatureUrl'; label: string; hint: string }) => {
    const value = form?.[field] ?? '';
    const isImg = value.startsWith('data:image/') || /^https?:\/\//.test(value);
    return (
      <div className="brand-img">
        <div className="brand-img__label">{label}<span className="faint"> · {hint}</span></div>
        <div className="brand-img__row">
          <div className="brand-img__preview">
            {isImg ? <img src={value} alt={label} /> : <span className="faint">Sin imagen</span>}
          </div>
          <div className="brand-img__actions">
            <label className="btn btn--ghost btn--sm">
              Subir imagen
              <input type="file" accept="image/*" style={{ display: 'none' }}
                onChange={(e) => { void onPickImage(field, e.target.files?.[0]); e.currentTarget.value = ''; }} />
            </label>
            {value && form && <button className="btn btn--ghost btn--sm" onClick={() => setForm({ ...form, [field]: '' })}>Quitar</button>}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="modal-scrim" onMouseDown={onClose}>
      <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal__head">
          <div><span className="eyebrow">Marca y sello digital</span><h2 className="h2" style={{ marginTop: 4, fontSize: 18 }}>{name}</h2></div>
          <button className="icon-btn" onClick={onClose}><Icon name="x" size={18} /></button>
        </div>
        <div className="modal__body">
          {!form ? <div className="faint">Cargando…</div> : (
            <>
              <p className="faint" style={{ fontSize: 12.5, marginBottom: 16 }}>Estos datos aparecen en las cotizaciones, contratos y facturas de esta empresa, y en la página de firma. El sello y la firma se estampan en todos los documentos.</p>

              <ImageField field="logoUrl" label="Logotipo" hint="cabecera del documento" />
              <ImageField field="stampUrl" label="Sello / cuño digital" hint="se estampa junto a la firma" />
              <ImageField field="companySignatureUrl" label="Firma del representante" hint="firma manuscrita de la empresa" />

              <div className="row gap-12 wrap" style={{ marginTop: 8 }}>
                <div className="field grow" style={{ minWidth: 180 }}><label>Firmante</label><input className="input" value={form.signatoryName} onChange={(e) => setForm({ ...form, signatoryName: e.target.value })} placeholder="Nombre y apellidos" /></div>
                <div className="field grow" style={{ minWidth: 180 }}><label>Cargo del firmante</label><input className="input" value={form.signatoryTitle} onChange={(e) => setForm({ ...form, signatoryTitle: e.target.value })} placeholder="p. ej. Director General" /></div>
              </div>
              <div className="field"><label>Color de marca <span className="faint">(solo en el PDF)</span></label>
                <div className="row gap-8 center"><input type="color" value={form.brandColor} onChange={(e) => setForm({ ...form, brandColor: e.target.value })} style={{ width: 36, height: 36, border: 'none', background: 'none', cursor: 'pointer' }} /><input className="input" value={form.brandColor} onChange={(e) => setForm({ ...form, brandColor: e.target.value })} style={{ maxWidth: 140 }} /></div>
              </div>
              <div className="field"><label>Pie de documento</label><input className="input" value={form.documentFooter} onChange={(e) => setForm({ ...form, documentFooter: e.target.value })} placeholder="Razón social · dirección · contacto" /></div>
              <div className="field"><label>Línea bancaria</label><input className="input" value={form.bankLine} onChange={(e) => setForm({ ...form, bankLine: e.target.value })} placeholder="Banco · IBAN" /></div>
              {err && <div className="act-error"><Icon name="alert" size={16} />{err}</div>}
              {saved && <div className="ok-note"><Icon name="check" size={16} />Marca y sello guardados.</div>}
            </>
          )}
        </div>
        <div className="modal__foot">
          <button className="btn btn--ghost" onClick={onClose}>Cerrar</button>
          <button className="btn btn--primary" onClick={save} disabled={busy || !form}>{busy ? 'Guardando…' : 'Guardar'}</button>
        </div>
      </div>
    </div>
  );
}

export function Companies() {
  const { company, refresh } = useSession();
  const [rows, setRows] = useState<Company[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [branding, setBranding] = useState<{ id: string; name: string } | null>(null);

  const load = () => {
    setRows(null);
    api.get<Company[]>('/companies').then(setRows).catch(() => setRows([]));
  };
  useEffect(load, [company]);

  return (
    <main className="main">
      <div className="page-head">
        <div className="page-head__row">
          <div className="page-head__title">
            <span className="eyebrow">Administration</span>
            <h1 className="h1">Empresas</h1>
            <p className="page-head__sub">Your group companies. Each has its own trade license, which determines what operations it can perform.</p>
          </div>
          <button className="btn btn--primary" onClick={() => setCreating(true)}><Icon name="plus" size={16} />Nueva empresa</button>
        </div>
      </div>

      {!rows && <div className="faint" style={{ padding: 40 }}>Loading…</div>}
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))' }}>
        {rows?.map((c) => {
          const days = daysTo(c.licenseExpiryDate);
          const licTone = days == null ? 'muted' : days < 0 ? 'broken' : days <= 30 ? 'broken' : days <= 60 ? 'warn' : 'active';
          return (
            <div key={c.id} className="card">
              <div className="card__body">
                <div className="row gap-12 center">
                  <span className="company-avatar" style={{ background: tone(c.id), width: 40, height: 40, fontSize: 15 }}>{initials(c.tradeName ?? c.legalName)}</span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>{c.tradeName ?? c.legalName}</div>
                    <div className="faint" style={{ fontSize: 12 }}>{c.legalName}</div>
                  </div>
                </div>

                <div style={{ marginTop: 16 }}>
                  <div className="meta__row"><span className="meta__k">Jurisdiction</span><span className="meta__v">{c.jurisdiction ?? c.country ?? '—'}</span></div>
                  <div className="meta__row"><span className="meta__k">Moneda</span><span className="meta__v num">{c.functionalCurrency}</span></div>
                  <div className="meta__row"><span className="meta__k">Licencia</span><span className="meta__v num">{c.licenseNumber ?? '—'}</span></div>
                  <div className="meta__row">
                    <span className="meta__k">Caducidad</span>
                    <span className={`pill pill--${licTone}`}><span className="pill__dot" />{c.licenseExpiryDate ? date(c.licenseExpiryDate) : 'Sin fecha'}</span>
                  </div>
                </div>

                {c.activities.length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <div className="eyebrow" style={{ marginBottom: 7 }}>Actividades</div>
                    <div className="cp-kinds">
                      {c.activities.slice(0, 5).map((a, i) => <span key={i} className="type-tag">{a}</span>)}
                    </div>
                  </div>
                )}
                {c.sectors.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div className="cp-kinds">
                      {c.sectors.map((s, i) => <span key={i} className="cp-kind">perfil: {s}</span>)}
                    </div>
                  </div>
                )}

                <button className="btn btn--ghost btn--sm" style={{ marginTop: 16 }} onClick={() => setBranding({ id: c.id, name: c.tradeName ?? c.legalName })}>
                  <Icon name="sliders" size={15} />Marca del documento
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {creating && <CreateModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); void refresh(); }} />}
      {branding && <BrandingModal companyId={branding.id} name={branding.name} onClose={() => setBranding(null)} />}
    </main>
  );
}
